import { DatePipe, NgClass, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectorRef } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { DevstroupeFullscreenComponent } from '@devstroupe/components/fullscreen';
import { DevstroupeLoadingBarComponent } from '@devstroupe/components/loading-bar';
import { DevstroupeNavigationItem, DevstroupeNavigationService, DevstroupeVerticalNavigationComponent } from '@devstroupe/components/navigation';
import { DevstroupeMediaWatcherService } from '@devstroupe/services/media-watcher';
import { AuthService } from 'app/core/auth/auth.service';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { CompanySelectorComponent } from 'app/layout/common/company-selector/company-selector.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { ApiService } from 'app/shared/apis/api.service';
import { TitleService } from 'app/shared/services/title.service';
import { log } from 'console';
import { debounceTime, filter, map, Subject, switchMap, takeUntil } from 'rxjs';
import { UserModel } from 'app/models/user.model';
import { DevstroupeConfig, DevstroupeConfigService } from '@devstroupe/services/config';
import { MatTooltipModule } from '@angular/material/tooltip';
import { StorageKeysService } from 'app/shared/services/storage-keys.service';

@Component({
    selector: 'classic-layout',
    templateUrl: './classic.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        DevstroupeLoadingBarComponent,
        DevstroupeVerticalNavigationComponent,
        MatButtonModule,
        MatIconModule,
        UserComponent,
        NgIf,
        RouterOutlet,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        NgClass,
        CompanySelectorComponent,
        MatTooltipModule,
    ],
})
export class ClassicLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    navigation: Navigation;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    navigationData: DevstroupeNavigationItem[];
    pageTitle: string = '';
    user: UserModel;
    config = {
        logo: 'assets/images/logo/logo.png',
        logoDark: 'assets/images/logo/artsys-logo-white.png',
        class: 'bg-white dark:bg-gray-900 text-primary-900 dark:text-white',
    };
    isDark: boolean = false;
    isAuto: boolean = false;

    /**
     * Constructor
     */ constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _devstroupeMediaWatcherService: DevstroupeMediaWatcherService,
        private _devstroupeNavigationService: DevstroupeNavigationService,
        private _titleService: TitleService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _apiService: ApiService,
        private _devstroupeConfigService: DevstroupeConfigService,
        private _storageKeysService: StorageKeysService,
    ) {
        this.updateFromRouteSnapshot();
        this._router.events
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((e) => e instanceof NavigationEnd),
                map(() => this.getDeepestRouteSnapshot()),
            )
            .subscribe((snapshot) => {
                this.ignoreSearch = snapshot?.data?.ignoreSearch ?? false;
            });
    }
    private intervalId: any;
    searchInputControl: UntypedFormControl = new UntypedFormControl();
    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    version_app: string = '';
    ignoreSearch: boolean = false;
    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.version_app = localStorage.getItem(this._storageKeysService.versao);
        ApiService.userCurrent.pipe(takeUntil(this._unsubscribeAll)).subscribe((user) => {
            this.user = user;
        });
        // Subscribe to title changes
        this._titleService.title$.pipe(takeUntil(this._unsubscribeAll)).subscribe((title: string) => {
            this.pageTitle = title;
            // Manually trigger change detection to avoid ExpressionChangedAfterItHasBeenCheckedError
            this._changeDetectorRef.detectChanges();
        });
        this._devstroupeConfigService.config$.pipe(takeUntil(this._unsubscribeAll)).subscribe((config: DevstroupeConfig) => {
            // Store the config
            // this.config = config;
            this.isDark = config.scheme == 'dark' || (config.scheme == 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches);
            this.isAuto = config.scheme == 'auto';
        });

        // Subscribe to navigation data
        this._navigationService.navigation$.pipe(takeUntil(this._unsubscribeAll)).subscribe((navigation: Navigation) => {
            this.navigation = navigation;
            this.setNavigationData();
        });
        this._activatedRoute.data.pipe(takeUntil(this._unsubscribeAll)).subscribe(({ config, ignoreSearch }) => {
            if (config) {
                this.config = config;
            }

            // if (ignoreSearch) {
            //     this.ignoreSearch = true;
            // }
        });

        this._activatedRoute.queryParams.pipe(takeUntil(this._unsubscribeAll)).subscribe((params) => {
            this.searchInputControl.setValue(this._activatedRoute.snapshot.queryParams['text'] ?? '', { emitEvent: false });
        });

        this.searchInputControl.valueChanges
            .pipe(
                takeUntil(this._unsubscribeAll),
                debounceTime(500),
                switchMap((query) => {
                    this._router.navigate([], {
                        relativeTo: this._activatedRoute,
                        queryParams: {
                            text: query,
                        },
                        queryParamsHandling: 'merge',
                    });
                    return query;
                }),
            )
            .subscribe();

        this._router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll),
            )
            .subscribe((event) => {
                if (!this._activatedRoute.snapshot.queryParams['text']) {
                    this.searchInputControl.setValue(this._activatedRoute.snapshot.queryParams['text'] ?? '', { emitEvent: false });
                }
            });
        // Subscribe to media changes
        this._devstroupeMediaWatcherService.onMediaChange$.pipe(takeUntil(this._unsubscribeAll)).subscribe(({ matchingAliases }) => {
            // Check if the screen is small
            this.isScreenSmall = !matchingAliases.includes('md');
        });

        // Verifica se há um scheme salvo no localStorage
        const savedScheme = localStorage.getItem(this._storageKeysService.scheme);
        if (savedScheme) {
            this._devstroupeConfigService.config = { scheme: savedScheme };
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        clearInterval(this.intervalId);
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._devstroupeNavigationService.getComponent<DevstroupeVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    setNavigationData(): void {
        if (!this.navigation) return;
        this.navigationData = this.navigation.classic;
    }

    setThemeAuto() {
        if (this.isAuto && window.matchMedia('(prefers-color-scheme: dark)').matches) {
            this.setTheme('dark');
        } else {
            this.setTheme('auto');
        }
    }

    setTheme(scheme) {
        this._devstroupeConfigService.config = { scheme };
    }

    private updateFromRouteSnapshot() {
        const snapshot = this.getDeepestRouteSnapshot();
        this.ignoreSearch = snapshot?.data?.ignoreSearch ?? false;
    }

    private getDeepestRouteSnapshot() {
        let r = this._activatedRoute.root;
        while (r.firstChild) r = r.firstChild;
        return r.snapshot;
    }
}
